import { _2d } from "../../../utils/numberUtils";
import { Evaluacion, UNIDAD } from "./evaluacion";

/**
 * Formatea un valor del monitoreo o un umbral.
 *
 * **La misma función para los dos a propósito.** Un valor y el umbral contra el
 * que se lo compara tienen que verse con el mismo criterio, o el semáforo deja
 * de poder verificarse: antes los umbrales se imprimían crudos (`0.25`) y los
 * valores forzados a un decimal (`0.3`), así que la pill decía "alerta: 0.25"
 * y el lote decía "0.3".
 *
 * Los valores **no son enteros**: el nivel es un promedio por árbol
 * (Σcantidad / nArboles). Y hay cultivos —papa, sobre todo— con umbrales
 * definidos en decimales, así que redondear a un decimal perdía la lectura.
 *
 * - entero → sin decimales: `12` → "12"
 * - con decimales → hasta 2, sin ceros de relleno: `1.85` → "1.85", `12.40` → "12.4"
 * - detección que redondearía a cero → "<0.01", nunca "0"
 *
 * Ese último caso es el que importa: con una plaga crítica cualquier valor
 * mayor que cero pinta el lote en rojo, así que mostrar "0" al lado del rojo se
 * lee como un error de la aplicación. "<0.01" dice lo que pasa — se detectó
 * algo, por debajo de la resolución que se muestra.
 */
export function formatValor(valor: number): string {
  if (valor > 0 && Number(valor.toFixed(2)) === 0) return "<0.01";
  return _2d(valor);
}

/**
 * Nombre corto de cada unidad, para escribirlo al lado del número.
 *
 * Sin la unidad el número no se puede leer: "0,38" puede ser ácaros por lupada
 * —que contra un umbral de 0,2 es alerta— o individuos por árbol, que contra el
 * mismo umbral significa otra cosa. Y el agricultor no tiene de dónde deducir
 * cuál es.
 */
const SUFIJO_UNIDAD: Record<string, string> = {
  [UNIDAD.acarosLupada]: "ác/lupada",
  [UNIDAD.individuosArbol]: "ind/árbol",
  [UNIDAD.porcentajeArboles]: "% árboles",
  [UNIDAD.presenciaCritica]: "árboles",
};

/**
 * El nivel con su unidad escrita: `"0.38 ác/lupada"`, `"2.4 ind/árbol"`,
 * `"3 árboles"` en los críticos.
 *
 * ⚠️ **Para los ácaros el número cambia respecto de lo que se mostraba antes.**
 * Se imprimía el promedio crudo del conteo; ahora se imprime el nivel, que es
 * ese promedio dividido entre las 4 lupadas por árbol. Es el cambio correcto:
 * el umbral está expresado **por lupada**, así que el promedio sin dividir no
 * se podía comparar con su propio umbral —la pill decía "alerta desde 0,2" al
 * lado de un valor de 6,1 que en realidad era 1,52—.
 *
 * Devuelve `"—"` cuando la unidad no define un nivel (los organismos que se
 * miden solo por porcentaje de árboles): ahí el número que importa es la
 * incidencia, y un cero inventado se leería como "no hay nada".
 */
export function formatNivel(evaluacion: Evaluacion): string {
  if (evaluacion.nivel === null) return "—";
  const sufijo =
    SUFIJO_UNIDAD[evaluacion.unidad] ?? SUFIJO_UNIDAD[UNIDAD.individuosArbol];
  return `${formatValor(evaluacion.nivel)} ${sufijo}`;
}

/**
 * La incidencia como porcentaje entero: `"33%"`.
 *
 * Entero y no con decimales porque es un porcentaje de árboles sobre una
 * muestra de decenas: con 21 árboles evaluados el salto mínimo es de casi 5
 * puntos, así que un decimal sugeriría una precisión que la muestra no tiene.
 */
export function formatPorcentaje(valor: number): string {
  return `${Math.round(valor)}%`;
}

/**
 * @deprecated Renombrada a {@link formatValor} (especificación v1.0, 5.4). Se
 * mantiene mientras se migran los nueve archivos que la usan.
 */
export const formatIncidencia = formatValor;
